import { initEnv, isTestEnv } from '@tencent/pmd-tools/lib/env';
import { extendUrlParams, resolveUrlParams, msdkSuffix, getUrlPara } from '@tencent/pmd-tools/lib/url';
import { GAME_LOLM, getGame, GameLifeGids, GAME_DGD } from '@tencent/pmd-tools/lib/game';
import { getReportTK } from '@tencent/pmd-report';
import { getConfig } from '@tencent/pmd-config';
import { loader } from '@tencent/pmd-tools/lib/little-loader';
import { genQueryToStr } from 'src/component/logic/tools/query';


const inGamePath = [
  'ingame-gp',
  'ingame-pvp',
  'ingame-hlddz',
  'ingame-majiang',
  'ingame-lolm',
  'igame-pvp',
  'igame-hlddz',
  'igame-majiang',
  'igame-lolm',
];

/**
 * @description 判断是否已在微社区、物品表，方便只做路由切换
 * @returns {boolean}
 */
function isInGamePath() {
  let isInGamePath = false;

  const projectName = getConfig('projectName');
  if (projectName) {
    const projectNameList = projectName.split('/');
    projectNameList.forEach((projectItem) => {
      inGamePath.forEach((item) => {
        if (projectItem === item) {
          isInGamePath = true;
        }
      });
    });
  }

  return isInGamePath && !initEnv().isLolApp;
}

/**
 * @description 根据 gid 获取跳转项目路径前缀
 * @param {Number} params.gid 游戏GID
 * @returns
 */
function getJumpProject({ gid } = {}) {
  const env = initEnv();
  const gameGid = gid || getConfig('game.gid') || app.tip_gid;
  if (gameGid === GAME_DGD && env.isInGame) {
    return 'ingame-game-life';
  }
  if (GameLifeGids.includes(gameGid)) {
    return 'igame-game-life';
  }
  return (env.isInGame ? 'ingame-' : 'igame-') + getGame(gameGid).tag;
}

/**
 * 仅模块内使用，获取当前环境下的igame域名
 * @deprecated
 * @returns {string} 域名
 */
function getIgameOrigin() {
  return isTestEnv() ? 'test.igame.qq.com' : 'igame.qq.com';
}

/**
 * 仅模块内使用，获取当前环境下的igame域名
 * @returns {string} 域名
 */
const getUrlOrigin = function ({ gid } = {}) {
  const gameGid = gid || getConfig('game.gid') || app.tip_gid;
  if (gameGid === GAME_LOLM) {
    return `https://h5${isTestEnv() ? '-test' : ''}.nes.slol.qq.com`;
  }

  if (window.location.origin === 'https://h5-test.igame.qq.com') {
    return 'https://test.igame.qq.com';
  }
  if (window.location.origin === 'https://h5.igame.qq.com') {
    return 'https://igame.qq.com';
  }
  if (window.location.origin) {
    return window.location.origin;
  }
  return `https://${isTestEnv() ? 'test.' : ''}igame.qq.com`;
};

/**
 * 仅模块内使用，处理页面参数
 * @description 1)添加外部传入参数 2)添加 reporttk参数
 * @param {string} pageUrl  页面地址
 * @param {object} extUrlParams 额外参数
 * @param {boolean} forceHistoryMode  是否强制使用history模式
 * @returns {string} 链接地址
 */
function handleUrlParams(pageUrl = '', extUrlParams = {}, forceHistoryMode = false) {
  if (!pageUrl) return pageUrl;

  // 额外参数
  const pageUrlParams = extUrlParams;

  // reporttk 参数：用于数据上报识别为同一次访问行为链路
  const reporttk = getReportTK();
  if (reporttk) {
    pageUrlParams.reporttk = reporttk;
  }

  return extendUrlParams(pageUrl, pageUrlParams, forceHistoryMode);
}


/**
 * 仅模块内使用，获取页面参数
 * @description 1)添加外部传入参数 2)添加 reporttk参数
 * @param {String} params.pageUrl  页面地址
 * @param {Object} params.extUrlParams 额外参数
 * @returns {string} 链接参数对象
 */
function getUrlParams({ pageUrl = '', extUrlParams = {} }) {
  const reporttk = getReportTK();
  const urlParams = {
    ...resolveUrlParams(pageUrl),
    ...extUrlParams,
    reporttk,
  };

  return urlParams;
}


/**
 * @description 根据 gid 获取跳转页面路径前缀
 * @returns
 */
function getPagePath({ gid } = {}) {
  const gameGid = gid || getConfig('game.gid') || app.tip_gid;

  if (gameGid === GAME_LOLM) {
    return '/slolnes.web.';
  }
  return '/tip/ingame-page/';
}

/**
 * 通过 jssdk 打开微社区新窗口
 * @param {string} url 页面地址
 * @param {boolean} useAnimation 是否需要过渡动画
 */
function slugOpenNewWindow(url, useAnimation = false) {
  // v94 版微社区
  if (typeof window.customBrowserInterface !== 'undefined') {
    window.customBrowserInterface.openNewWindow(url, useAnimation);
    return;
  }

  // 没有找到浏览器 jssdk
  loader('https://tiem-cdn.qq.com/slugteam/sdk/latest/browser_adapt.js')
    .then(() => {
      window.customBrowserInterface.openNewWindow(url, useAnimation);
    });
}

/**
 * @description 同一个项目下的页面路径切换
 * @param {String} path 页面路径
 * @param {Boolean} [params.isReplace=false] 是否采用 window.location.replace（常用于避免跳转循环）
 */
function jumpToPage(path, options = {}) {
  const { isReplace = false, branch = '' } = options;
  const isUrl = path.startsWith('http');
  const projectUrl = `${getUrlOrigin(options)}${getPagePath(options)}${branch}${getJumpProject(options)}`;
  const url = isUrl ? path : `${projectUrl}/#${path}${msdkSuffix()}`;
  if (window.$wujie?.props?.jumpToPage) {
    window.$wujie.props.jumpToPage(url, options);
  } else if (getUrlPara('pagesource') === 'box' && /ESTV/i.test(navigator.userAgent)) {
    slugOpenNewWindow(url, true);
  } else {
    const env = initEnv();
    const jump = function (url, isReplace = false, currentWindow = window) {
      const global = currentWindow;
      if (isReplace) {
        global.location.replace(url);
        return;
      }

      global.location.href = url;
    };

    if (env.isLolApp) {
      jump(`qtpage://news_detail?url=${encodeURIComponent(url)}`, isReplace);
      return;
    }

    if (!isUrl && window.location.href.startsWith(`${projectUrl}/`)
      && window.location.href.indexOf(path) === -1
      && !window.location.href.startsWith(`${projectUrl}-wsq`)
      && (env.isInGame || env.isWeixin || env.isQQ)) {
      if (isReplace) {
        // #ifdef VUE2
        app.$router.replace({ path });
        // #endif
        // #ifdef VUE3
        uni.redirectTo({ url: path });
        // #endif
      } else {
        // #ifdef VUE2
        app.$router.push({ path });
        // #endif
        // #ifdef VUE3
        uni.navigateTo({ url: path });
        // #endif
      }
      return;
    }

    if (!(env.isMPWeixin || env.isMPQQ) && self !== top) {
      jump(url, isReplace, top);
      return;
    }

    jump(url, isReplace);
  }
}

export {
  isInGamePath,
  getIgameOrigin,
  getUrlOrigin,
  handleUrlParams,
  getUrlParams,
  getJumpProject,
  getPagePath,
  slugOpenNewWindow,
  jumpToPage,
  GameLifeGids,
};

export const jumpToPageCommon = ({ path, name, query = {} }) => {
  // #ifdef VUE2
  if (name) {
    app.$router.push({ name, query });
  } else {
    app.$router.push({ path, query });
  }
  // #endif
  // #ifdef VUE3
  const queryStr = genQueryToStr(query);
  uni.navigateTo({ url: `${path}${queryStr ? `?${queryStr}` : ''}` });
  // #endif
};
